import { ContactDetails, ValidationError } from '../types';

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validatePhone(phone: string): boolean {
  // E.164 or (###) ###-####
  const e164Regex = /^\+?[1-9]\d{1,14}$/;
  const usFormatRegex = /^\(\d{3}\)\s?\d{3}-\d{4}$/;
  return e164Regex.test(phone.replace(/[\s()-]/g, '')) || usFormatRegex.test(phone);
}

export function validateUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

export function validateContactDetails(
  contact: ContactDetails,
  serviceId: string
): ValidationError[] {
  const errors: ValidationError[] = [];

  const isSupport = serviceId === 'support';
  const isKickoff = serviceId === 'kickoff';
  const isSimplified = isSupport || isKickoff;

  if (!contact.firstName.trim()) {
    errors.push({ field: 'firstName', message: 'First name is required' });
  }

  if (!contact.lastName.trim()) {
    errors.push({ field: 'lastName', message: 'Last name is required' });
  }

  if (!contact.email.trim()) {
    errors.push({ field: 'email', message: 'Email is required' });
  } else if (!validateEmail(contact.email)) {
    errors.push({ field: 'email', message: 'Please enter a valid email address' });
  }

  if (!contact.agency.trim()) {
    errors.push({ field: 'agency', message: 'Agency name is required' });
  }

  if (!isSimplified && !contact.state.trim()) {
    errors.push({ field: 'state', message: 'State is required' });
  }

  if (!isSimplified) {
    if (contact.referredBy === 'none') {
      errors.push({ field: 'referredBy', message: 'Please select how you heard about us' });
    }

    if (contact.referredBy === 'other' || contact.referredBy === 'personal_referral') {
      if (!contact.referredByOther?.trim()) {
        const message = contact.referredBy === 'personal_referral'
          ? 'Please tell us who referred you'
          : 'Please tell us how you heard about us';
        errors.push({ field: 'referredByOther', message });
      }
    }
  }

  if (isSupport) {
    if (!contact.problems || !contact.problems.trim()) {
      errors.push({ field: 'problems', message: 'Please tell us what you need help with' });
    }
  }

  // Optional field validation
  if (contact.agencyPhone && !validatePhone(contact.agencyPhone)) {
    errors.push({ field: 'agencyPhone', message: 'Please enter a valid phone number' });
  }

  if (contact.agencyWebsite) {
    try {
      const raw = contact.agencyWebsite.trim();
      const normalized = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
      const url = new URL(normalized);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new Error();
      }
      contact.agencyWebsite = normalized;
    } catch {
      errors.push({ field: 'agencyWebsite', message: 'Please enter a valid website URL' });
    }
  }

  if (contact.agencySize !== undefined && contact.agencySize < 1) {
    errors.push({ field: 'agencySize', message: 'Agency size must be at least 1' });
  }

  if (contact.attendeeCount !== undefined && contact.attendeeCount < 1) {
    errors.push({ field: 'attendeeCount', message: 'Attendee count must be at least 1' });
  }

  if (contact.problems && contact.problems.length > 2000) {
    errors.push({ field: 'problems', message: 'Please limit your response to 2000 characters' });
  }

  return errors;
}
